import { replace } from 'lodash'
import numeral from 'numeral'

// ----------------------------------------------------------------------

export function fCurrency(number: string | number) {
  return numeral(number).format(Number.isInteger(number) ? '$0,0' : '$0,0.00')
}

export function fPercent(number: number) {
  return numeral(number / 100).format('0.0%')
}

export function fNumber(number: string | number, options?: { disabledDecimal: boolean }) {
  const num = Number(number)
  if (isNaN(num)) return `${number}`

  if (options) {
    if (options.disabledDecimal) return numeral(number).format('0,0')
  }
  return numeral(number).format('0,0.00')
}

/**
 * Format number with thousand separators and show decimals only when needed.
 */
export function fNumberOptionalDecimal(number: string | number) {
  const num = Number(number)
  if (isNaN(num)) return `${number}`

  return numeral(number).format(Number.isInteger(num) ? '0,0' : '0,0.00')
}

/**
 * Format with thousand separators, keeping up to 2 decimals and trimming
 * trailing zeros (14820 -> "14,820", 82.4 -> "82.4", 1.239 -> "1.24").
 */
export function fNumberTrimmed(number: string | number) {
  const num = Number(number)
  if (isNaN(num)) return `${number}`

  return numeral(number).format('0,0.[00]')
}

export function f3Number(number: string | number, options?: { disabledDecimal: boolean }) {
  if (options) {
    if (options.disabledDecimal) return numeral(number).format()
  }
  return numeral(number).format('0,0.000')
}

export function fShortenNumber(number: string | number) {
  return replace(numeral(number).format('0.00a'), '.00', '')
}

export function fData(number: string | number) {
  return numeral(number).format('0.0 b')
}

export function formatNumber(val: string | number) {
  return numeral(val).format(Number.isInteger(val) ? '0' : '0.[00]')
}

// Unformat a number-like string by removing commas and trimming spaces
export const unformatNumberString = (val: string) => val.replace(/,/g, '').trim();

// Format with commas while preserving decimals (no rounding) and trailing dot during typing
export const formatWithCommasPreserveDecimals = (val: string | number) => {
  if (val === null || val === undefined || val === '-') return '';
  const s = String(val).replace(/,/g, '').trim();
  if (!s) return '';

  // Handle leading sign
  let sign = '';
  let body = s;
  if (s[0] === '+' || s[0] === '-') {
    sign = s[0] === '-' ? '-' : '';
    body = s.slice(1);
  }

  const hasTrailingDot = body.endsWith('.');
  const parts = body.split('.');
  const intRaw = parts[0] || '0';
  const decRaw = parts[1] ?? undefined; // may be undefined

  // Add commas to integer part
  const intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (hasTrailingDot) {
    return sign + intFormatted + '.'; // preserve trailing '.' while typing
  }
  if (decRaw !== undefined) {
    return sign + intFormatted + (decRaw !== '' ? '.' + decRaw : '');
  }
  return sign + intFormatted;
};

/**
 * Format a number or numeric string with commas for display.
 */
export const formatNumberWithCommas = (value: string | number | null | undefined): string => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  return isNaN(num) ? '' : num.toLocaleString('en-US');
};

/**
 * Parse a formatted number string (e.g. "1,234.56") into a numeric value or empty string.
 */
export const parseNumberInput = (input: string): number | '' => {
  const raw = input.replace(/,/g, '');
  if (!/^\d*\.?\d*$/.test(raw)) return '';
  return raw === '' ? '' : Number(raw);
};

// Matches an in-progress decimal: whole number, "5.", ".5", "5.5", or empty.
const DECIMAL_IN_PROGRESS = /^(\d+(\.\d*)?|\.\d*)$/;

/**
 * Sanitize a raw input for a decimal number field, stripping commas and
 * preserving a decimal-in-progress (e.g. "5." or ".5") so it survives a
 * controlled re-render. Returns the cleaned string, or null when the input is
 * not a valid (partial) decimal — callers should drop the keystroke on null.
 */
export const sanitizeDecimalInput = (input: string): string | null => {
  const raw = unformatNumberString(input);
  if (raw !== '' && !DECIMAL_IN_PROGRESS.test(raw)) return null;
  return raw;
};
